// The sankey funnel: bars centered on a midline, ribbons between them, drop
// bands for journeys that ended, and an optional bypass band for a step that
// skips a column. Geometry comes from `layoutFunnel` (lib/funnel-geometry.ts);
// this component is only the SVG for it.
//
// Everything app-specific is a prop: COLOR is resolved by the caller
// (`colorForStage`, default --rc-chart-1..8 slots; the house rule in
// chart-color.ts), and HOVER/ACTIVATION are semantic events over a
// FunnelHoverTarget. Omit `onActivate` and no click affordance is drawn.
//
// Each bar splits into a primary segment over a hatched SECONDARY segment
// (e.g. signed-in over guest); ribbons carry the same split; drops converge
// into one stub per stage; the bypass sags under the column it skips.

import { useId, useMemo } from "react";

import {
  formatPct,
  layoutFunnel,
  sharePct,
  type FunnelStageDatum,
  type FunnelTransitionDatum,
} from "../../lib/funnel-geometry";
import { categoricalSlotVar } from "./chart-color";
import "./funnel-chart.css";

/** What the pointer (or keyboard focus) is on. The consumer turns this into a
 *  tooltip and/or a destination; the chart itself stays presentational. */
export type FunnelHoverTarget =
  | { kind: "node"; key: string; index: number }
  | { kind: "band"; fromKey: string; toKey: string; count: number; secondaryCount: number }
  | { kind: "drop"; key: string; stalled: number }
  | { kind: "bypass"; fromKey: string; toKey: string; count: number; secondaryCount: number };

export interface FunnelChartProps {
  stages: readonly FunnelStageDatum[];
  transitions: readonly FunnelTransitionDatum[];
  /** Resolved CSS color for a stage's bar. Defaults to the categorical slots. */
  colorForStage?: (key: string, index: number) => string;
  /** Resolved CSS color for ONE ribbon. Return undefined for the neutral default.
   *  Separate from `colorForStage` so a caller can tint only the one or two
   *  ribbons that carry a meaning, not every ribbon by destination. */
  colorForBand?: (fromKey: string, toKey: string) => string | undefined;
  /** Resolved CSS color for the bypass band. Defaults to the accent. */
  bypassColor?: string;
  /** Label for the hatched sub-series, used only in the accessible summary, and
   *  only when there IS one. */
  secondarySeriesLabel?: string;
  /** What one unit of this funnel IS, plural, for the accessible copy. The
   *  numbers are the whole message for a screen-reader user, and "80" without a
   *  noun is not a message. The default is deliberately the neutral word: this
   *  primitive draws whatever a BigQuery funnel counts, so a caller that knows
   *  its own noun says it, and one that does not is vague rather than wrong. */
  unitLabel?: string;
  /** How every figure this chart PRINTS is rendered: the count under each bar,
   *  the drop and bypass labels, and the accessible names. Defaults to the plain
   *  integer. Lets a value-weighted funnel print dollars without a second SVG.
   *  Percentages stay with `formatPct`: a share is a share whatever the bars
   *  measure. */
  formatValue?: (value: number) => string;
  onHover?: (target: FunnelHoverTarget, event: React.PointerEvent | React.FocusEvent) => void;
  onLeave?: () => void;
  /** Omit to render a chart with no click affordance and no button roles. */
  onActivate?: (target: FunnelHoverTarget) => void;
  /** Which targets `onActivate` actually handles. Per-target because a caller
   *  often acts on some marks (a node) but not others (a ribbon has no stage to
   *  filter by); an unhandled mark must not get a pointer cursor or button role.
   *  Defaults to every target when `onActivate` is given. */
  isActivatable?: (target: FunnelHoverTarget) => boolean;
  /** Appended to the accessible name of every mark that IS activatable, e.g.
   *  "Open in Explorer." A `role="button"` that does not say what the button
   *  does is a worse affordance than no button at all, and a screen-reader user
   *  has nothing else to go on: the pointer affordance is a cursor change they
   *  never see. */
  activateHint?: string;
  /** viewBox width. The SVG scales to its container; this sets the aspect. */
  width?: number;
  /** Accessible description. A sensible summary is generated when absent. */
  ariaLabel?: string;
}

export function FunnelChart({
  stages,
  transitions,
  colorForStage,
  colorForBand,
  bypassColor,
  secondarySeriesLabel = "secondary",
  unitLabel = "items",
  formatValue = (value: number) => String(value),
  onHover,
  onLeave,
  onActivate,
  isActivatable,
  activateHint,
  width = 840,
  ariaLabel,
}: FunnelChartProps) {
  const gradId = useId();
  const layout = useMemo(
    () => layoutFunnel(stages, transitions, width),
    [stages, transitions, width],
  );
  const cohort = stages[0]?.reached ?? 0;
  const byKey = useMemo(
    () => new Map(stages.map((s, i) => [s.key, { stage: s, index: i }])),
    [stages],
  );

  const stageColor = (key: string, index: number): string =>
    colorForStage?.(key, index) ?? categoricalSlotVar(index);

  const acts = (target: FunnelHoverTarget): boolean =>
    onActivate !== undefined && (isActivatable?.(target) ?? true);
  // A hit area earns a tab stop only if focusing it DOES something: open the
  // tooltip, or activate. A read-only funnel with neither (the BI app today)
  // would otherwise hand a keyboard user one dead stop per stage, per ribbon
  // and per drop, which is worse than not being focusable at all.
  const stop = (target: FunnelHoverTarget, first: boolean): number =>
    first && (acts(target) || onHover !== undefined) ? 0 : -1;
  /** The accessible name, plus the affordance hint when this mark can be
   *  activated. */
  const named = (target: FunnelHoverTarget, label: string | undefined): string | undefined =>
    label === undefined
      ? undefined
      : acts(target) && activateHint
        ? `${label} ${activateHint}`
        : label;
  const hit = (
    target: FunnelHoverTarget,
    d: string | null,
    { first, label }: { first: boolean; label?: string },
  ) => {
    const clickable = acts(target);
    return d === null ? null : (
      <path
        className={`rc-fn-hit${clickable ? " rc-fn-hit--click" : ""}`}
        d={d}
        tabIndex={stop(target, first)}
        role={first && clickable ? "button" : undefined}
        aria-label={first ? named(target, label) : undefined}
        onPointerMove={onHover ? (e) => onHover(target, e) : undefined}
        onFocus={onHover ? (e) => onHover(target, e) : undefined}
        onPointerLeave={onLeave}
        onBlur={onLeave}
        onClick={clickable && onActivate ? () => onActivate(target) : undefined}
        onKeyDown={
          clickable && onActivate
            ? (e) => {
                // Space on a role="button" must not ALSO scroll the page: this
                // is a path, not a button, so nothing does that for us.
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onActivate(target);
                }
              }
            : undefined
        }
      />
    );
  };

  // Only claim a sub-series when one is actually drawn: a single-series funnel
  // that narrates a hatch nobody can see is a description of a different chart.
  const hasSecondary = stages.some((s) => s.guestReached > 0);
  const summary =
    ariaLabel ??
    `Funnel of ${formatValue(cohort)} ${unitLabel}` +
      (hasSecondary ? `, with a ${secondarySeriesLabel} sub-series` : "") +
      ": " +
      stages
        .slice(1)
        .map((s) => `${formatPct(sharePct(s.reached, cohort))} ${s.label.toLowerCase()}`)
        .join(", ");

  const dropFade = `${gradId}-drop`;
  const laneFade = `${gradId}-lane`;
  const hatch = `${gradId}-hatch`;
  const laneInk = bypassColor ?? "var(--rc-color-accent)";

  return (
    <svg
      className="rc-fn-svg"
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      role="img"
      aria-label={summary}
    >
      <defs>
        <linearGradient id={dropFade} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" style={{ stopColor: "var(--rc-color-text-muted)" }} stopOpacity={0.42} />
          <stop offset="1" style={{ stopColor: "var(--rc-color-text-muted)" }} stopOpacity={0.06} />
        </linearGradient>
        <linearGradient id={laneFade} x1="0" y1="0" x2="1" y2="0.4">
          <stop offset="0" style={{ stopColor: laneInk }} stopOpacity={0.5} />
          <stop offset="1" style={{ stopColor: laneInk }} stopOpacity={0.1} />
        </linearGradient>
        {/* The secondary series: a diagonal hatch, unmistakably different from
            the solid primary flow in both themes. */}
        <pattern
          id={hatch}
          width="5"
          height="5"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="5" height="5" style={{ fill: "var(--rc-color-text-muted)" }} opacity={0.1} />
          <line
            x1="0"
            y1="0"
            x2="0"
            y2="5"
            style={{ stroke: "var(--rc-color-text-muted)" }}
            strokeWidth={1.7}
            opacity={0.55}
          />
        </pattern>
      </defs>

      {/* Drop bands first: journeys that ended at a column. Under the ribbons so
          a crossing reads as background, not clutter. */}
      {layout.drops.map((drop) => {
        const entry = byKey.get(drop.key);
        if (!entry) return null;
        const target: FunnelHoverTarget = {
          kind: "drop",
          key: drop.key,
          stalled: drop.stalled,
        };
        const label = `${formatValue(drop.stalled)} ${unitLabel} stopped at ${entry.stage.label}.`;
        return (
          <g key={`drop-${drop.key}`}>
            {drop.signedPath ? (
              <path className="rc-fn-drop" d={drop.signedPath} fill={`url(#${dropFade})`} />
            ) : null}
            {drop.guestPath ? (
              <path
                className="rc-fn-drop rc-fn-drop--secondary"
                d={drop.guestPath}
                fill={`url(#${hatch})`}
              />
            ) : null}
            <text className="rc-fn-drop-label" x={drop.labelX} y={drop.labelY + 3}>
              -{formatValue(drop.stalled)}
              <tspan className="rc-fn-drop-pct">
                {" "}
                {formatPct(sharePct(drop.stalled, entry.stage.reached))}
              </tspan>
            </text>
            {hit(target, drop.signedPath, { first: true, label })}
            {hit(target, drop.guestPath, { first: drop.signedPath === null, label })}
          </g>
        );
      })}

      {/* Ribbons over the drops: a primary band plus a hatched secondary band. */}
      {layout.bands.map((band) => {
        const entry = byKey.get(band.fromKey);
        const toEntry = byKey.get(band.toKey);
        const target: FunnelHoverTarget = {
          kind: "band",
          fromKey: band.fromKey,
          toKey: band.toKey,
          count: band.count,
          secondaryCount: band.guestCount,
        };
        const from = entry?.stage.label ?? band.fromKey;
        const to = toEntry?.stage.label ?? band.toKey;
        // undefined keeps the stylesheet's neutral flow ink. A caller tints the
        // one or two ribbons that carry a meaning, not all of them.
        const tint = colorForBand?.(band.fromKey, band.toKey);
        return (
          <g key={`${band.fromKey}->${band.toKey}`}>
            <path
              className={`rc-fn-band${tint ? " rc-fn-band--tinted" : ""}`}
              d={band.signedPath}
              style={tint ? { fill: tint } : undefined}
            />
            {band.guestPath ? (
              <path
                className="rc-fn-band rc-fn-band--secondary"
                d={band.guestPath}
                fill={`url(#${hatch})`}
              />
            ) : null}
            {hit(target, band.signedPath, {
              first: true,
              label: `${from} to ${to}: ${formatValue(band.count)} ${unitLabel}`,
            })}
            {hit(target, band.guestPath, { first: false })}
          </g>
        );
      })}

      {/* The bypass: a step that skips a column, sagging under it. */}
      {layout.bypass
        ? (() => {
            const bypass = layout.bypass;
            const target: FunnelHoverTarget = {
              kind: "bypass",
              fromKey: bypass.fromKey,
              toKey: bypass.toKey,
              count: bypass.count,
              secondaryCount: bypass.guestCount,
            };
            const label = `${formatValue(bypass.count)} ${unitLabel} took the direct path, skipping a stage.`;
            return (
              <g>
                {bypass.signedPath ? (
                  <path className="rc-fn-lane" d={bypass.signedPath} fill={`url(#${laneFade})`} />
                ) : null}
                {bypass.guestPath ? (
                  <path
                    className="rc-fn-lane rc-fn-lane--secondary"
                    d={bypass.guestPath}
                    fill={`url(#${hatch})`}
                  />
                ) : null}
                <text
                  className="rc-fn-lane-label"
                  x={bypass.labelX}
                  y={bypass.labelY + 3}
                  style={bypassColor ? { fill: bypassColor } : undefined}
                >
                  {formatValue(bypass.count)} direct
                </text>
                {hit(target, bypass.signedPath, { first: true, label })}
                {hit(target, bypass.guestPath, { first: bypass.signedPath === null, label })}
              </g>
            );
          })()
        : null}

      {/* Node bars (primary over secondary) + floating labels. */}
      {layout.nodes.map((node, i) => {
        const entry = byKey.get(node.key);
        if (!entry) return null;
        const isFirst = i === 0;
        const isLast = i === layout.nodes.length - 1;
        const anchor = isFirst ? "start" : isLast ? "end" : "middle";
        const labelX = isFirst
          ? node.x
          : isLast
            ? node.x + layout.barWidth
            : node.x + layout.barWidth / 2;
        const pct = isFirst ? null : sharePct(node.reached, cohort);
        const target: FunnelHoverTarget = { kind: "node", key: node.key, index: i };
        // Labels float just above their own bar, following the centered
        // silhouette instead of pinning to a ceiling.
        const nameY = Math.max(node.y - 26, 12);
        const countY = nameY + 17;
        const fill = stageColor(node.key, i);
        return (
          <g key={node.key}>
            <rect
              className="rc-fn-node"
              x={node.x}
              y={node.signed.y}
              width={layout.barWidth}
              height={Math.max(node.signed.h, node.reached > 0 ? 3 : 0)}
              rx={2}
              style={{ fill }}
            />
            {node.guest ? (
              <g>
                <rect
                  className="rc-fn-node rc-fn-seg--secondary"
                  x={node.x}
                  y={node.guest.y}
                  width={layout.barWidth}
                  height={node.guest.h}
                  rx={2}
                  style={{ fill }}
                />
                <rect
                  className="rc-fn-seg--secondary-hatch"
                  x={node.x}
                  y={node.guest.y}
                  width={layout.barWidth}
                  height={node.guest.h}
                  rx={2}
                  fill={`url(#${hatch})`}
                />
              </g>
            ) : null}
            <text className="rc-fn-node-label" x={labelX} y={nameY} textAnchor={anchor}>
              {node.label}
            </text>
            <text className="rc-fn-node-count" x={labelX} y={countY} textAnchor={anchor}>
              {formatValue(node.reached)}
              {pct !== null ? <tspan className="rc-fn-node-pct"> {formatPct(pct)}</tspan> : null}
            </text>
            <rect
              className={`rc-fn-hit${acts(target) ? " rc-fn-hit--click" : ""}`}
              x={node.x - 14}
              y={Math.max(nameY - 14, 0)}
              width={layout.barWidth + 28}
              height={node.y + Math.max(node.h, 24) - Math.max(nameY - 14, 0)}
              tabIndex={stop(target, true)}
              role={acts(target) ? "button" : undefined}
              aria-label={named(target, `${node.label}: ${formatValue(node.reached)} ${unitLabel}`)}
              onPointerMove={onHover ? (e) => onHover(target, e) : undefined}
              onFocus={onHover ? (e) => onHover(target, e) : undefined}
              onPointerLeave={onLeave}
              onBlur={onLeave}
              onClick={acts(target) && onActivate ? () => onActivate(target) : undefined}
              onKeyDown={
                acts(target) && onActivate
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onActivate(target);
                      }
                    }
                  : undefined
              }
            />
          </g>
        );
      })}
    </svg>
  );
}
